import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ChevronRight, Clock3, Search, Star } from "lucide-react";
import useIsMobile from "../hooks/useIsMobile";
import { MobileSheet, MobileSheetBody, MobileSheetContent, MobileSheetTitle } from "../components/ui/mobile-sheet";
import { accentOf, filterApps, groupByCategory, timeAgo } from "../lib/apps";
import { UPCOMING_LABEL } from "../lib/navigation";
import InstallButton from "../components/InstallButton";
import { bundleInfo, versionLabel, versionTitle } from "../lib/appVersion";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function Counter({ count, id }) {
  if (!(count > 0)) return null;
  return (
    <span className="shrink-0 rounded-full bg-amber-400 px-1.5 text-2xs font-semibold text-jamora" aria-label={`${count} en attente`} data-testid={`app-count-${id}`}>
      {count}
    </span>
  );
}

// A usable application: tinted icon, what it holds and what waits in it.
function AppTile({ app, waiting, favorite, onToggleFavorite, onOpenPages }) {
  const count = waiting?.total ?? 0;
  const accent = accentOf(app.accent);
  const pages = [...new Set(app.items.filter((item) => !item.upcoming).map((item) => item.group || item.label))];
  return (
    <li className="relative min-w-0">
      <Link to={app.to} data-testid={`app-tile-${app.id}`} onClick={onOpenPages ? (event) => { event.preventDefault(); onOpenPages(app); } : undefined}
        className={`group relative flex h-full flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5 transition hover:shadow-sm ${accent.hover}`}>
        <span className="flex items-start gap-3 pr-8">
          <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-black/10 ${accent.tile}`}>
            <app.icon size={24} aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="truncate text-base font-semibold text-slate-900">{app.label}</span>
              <Counter count={count} id={app.id} />
            </span>
            {app.description && <span className="mt-0.5 block text-sm text-slate-500">{app.description}</span>}
          </span>
        </span>
        {waiting?.texts?.length > 0 && (
          <span className="text-xs font-medium text-amber-700" data-testid={`app-waiting-${app.id}`}>{waiting.texts.join(" · ")}</span>
        )}
        {pages.length > 1 && (
          <span className="mt-auto truncate text-xs text-slate-400" data-testid={`app-pages-${app.id}`}>{pages.join(" · ")}</span>
        )}
      </Link>
      {app.items.length > 0 && (
        <button type="button" onClick={() => onToggleFavorite(app.id)} aria-pressed={favorite} data-testid={`app-fav-${app.id}`}
          aria-label={favorite ? `Retirer ${app.label} des favoris` : `Ajouter ${app.label} aux favoris`}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md text-slate-300 hover:bg-slate-50 hover:text-amber-500">
          <Star size={16} className={favorite ? "fill-amber-400 text-amber-500" : ""} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

// A planned module: small, greyed, not a link.
function UpcomingTile({ app }) {
  return (
    <li aria-disabled="true" title={UPCOMING_LABEL} data-testid={`app-tile-${app.id}`}
      className="flex items-center gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-4 py-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
        <app.icon size={18} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-500">{app.label}</span>
        {app.description && <span className="block truncate text-xs text-slate-400">{app.description}</span>}
      </span>
    </li>
  );
}

const RECENTS_SHOWN = 4;

// The applications centre: every domain the person can reach, one tile each, with what waits in it.
export default function Apps() {
  const { apps, tools, counts, favorites, toggleFavorite, recents } = useOutletContext();
  const [query, setQuery] = useState("");
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [pagesOf, setPagesOf] = useState(null);
  const compact = useIsMobile(1023);
  // Usable applications first; the planned ones (administrators only) go to their own section at the bottom.
  const matching = filterApps([...apps, ...tools], { query, onlyFavorites, favorites });
  const usable = matching.filter((app) => !app.upcoming);
  const upcomingApps = onlyFavorites ? [] : matching.filter((app) => app.upcoming);
  const nothing = !usable.length && !upcomingApps.length;

  return (
    <div className="space-y-8" data-testid="apps-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Applications</h2>
        <p className="mt-1 text-sm text-slate-500">Retrouvez tous les espaces de Jamora CRM auxquels vous avez accès.</p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-md">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une application" aria-label="Rechercher une application"
            className="pl-9" data-testid="apps-search" />
        </div>
        <Button type="button" variant={onlyFavorites ? "default" : "outline"} onClick={() => setOnlyFavorites((v) => !v)} aria-pressed={onlyFavorites}
          data-testid="apps-favorites-filter" className="gap-2">
          <Star size={16} aria-hidden="true" />
          Mes favoris
        </Button>
      </div>

      {recents.length > 0 && !query && !onlyFavorites && (
        <section aria-labelledby="apps-recents">
          <h3 id="apps-recents" className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <Clock3 size={14} aria-hidden="true" /> Dernières pages consultées
          </h3>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="apps-recents">
            {recents.slice(0, RECENTS_SHOWN).map((recent) => (
              <li key={recent.id}>
                <Link to={recent.item.to} data-testid={`apps-recent-${recent.id}`}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:border-jamora/40">
                  <recent.item.icon size={16} className="shrink-0 text-slate-500" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">{recent.item.label}</span>
                    <span className="block truncate text-xs text-slate-400">{[recent.appLabel, timeAgo(recent.at)].filter(Boolean).join(" · ")}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {nothing ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="apps-empty">
          <p className="text-sm font-medium text-slate-700">{onlyFavorites && !query ? "Aucune application en favori" : "Aucune application ne correspond"}</p>
          <p className="mt-1 text-sm text-slate-500">
            {onlyFavorites && !query ? "Touchez l'étoile d'une application pour la retrouver ici." : "Essayez un autre mot, ou affichez toutes les applications."}
          </p>
        </div>
      ) : (
        <>
          {groupByCategory(usable).map((group) => (
            <section key={group.id} aria-labelledby={`apps-cat-${group.id}`} data-testid={`apps-category-${group.id}`}>
              <h3 id={`apps-cat-${group.id}`} className="mb-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                {group.label}
                <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
              </h3>
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid={`apps-grid-${group.id}`}>
                {group.apps.map((app) => (
                  <AppTile key={app.id} app={app} waiting={counts[app.id] ?? (app.badge > 0 ? { total: app.badge, texts: [] } : null)} favorite={favorites.includes(app.id)} onToggleFavorite={toggleFavorite} onOpenPages={compact ? setPagesOf : undefined} />
                ))}
              </ul>
            </section>
          ))}
          {upcomingApps.length > 0 && (
            <section aria-labelledby="apps-upcoming" data-testid="apps-upcoming">
              <h3 id="apps-upcoming" className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Prochainement</h3>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {upcomingApps.map((app) => <UpcomingTile key={app.id} app={app} />)}
              </ul>
            </section>
          )}
        </>
      )}

      <div className="lg:hidden">
        <InstallButton testid="apps-install"
          className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50" />
        <p className="mt-3 text-xs text-slate-500" title={versionTitle(bundleInfo())} data-testid="apps-version">Version {versionLabel(bundleInfo().version)}</p>
      </div>
      <AppPagesSheet app={pagesOf} onClose={() => setPagesOf(null)} />
    </div>
  );
}

// Phone and tablet: touching an application lists its pages instead of jumping to the first one.
function AppPagesSheet({ app, onClose }) {
  const pages = app ? app.items.filter((item) => !item.upcoming) : [];
  return (
    <MobileSheet open={!!app} onOpenChange={(open) => { if (!open) onClose(); }}>
      <MobileSheetContent closeOnHandle className="lg:hidden" aria-describedby={undefined}>
        <MobileSheetTitle className="sr-only">{app?.label}</MobileSheetTitle>
        <MobileSheetBody>
          <nav className="space-y-0.5 pt-2" data-testid="apps-pages-sheet">
            <div className="flex items-center gap-2 px-2 pb-1 text-sm font-semibold text-slate-900">
              {app && <app.icon size={18} className="text-slate-500" aria-hidden="true" />}
              {app?.label}
            </div>
            {pages.map((item) => (
              <Link key={item.id} to={item.to} onClick={onClose} data-testid={`apps-pages-${item.testid}`}
                className="flex items-center gap-3 rounded-lg px-2 py-3 text-sm text-slate-700 hover:bg-slate-50">
                <item.icon size={18} className="shrink-0 text-slate-500" aria-hidden="true" />
                <span className="flex-1">{item.label}</span>
                <ChevronRight size={16} className="shrink-0 text-slate-300" aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </MobileSheetBody>
      </MobileSheetContent>
    </MobileSheet>
  );
}
